import { useCallback } from 'react';
import type { EntrySource, EventSource } from '@/consts/events';
import analytics from '@/services/Analytics';
import { useRegistration } from '@/context/RegistrationContext';

export type TrackArguments = {
  event: string;
  // `source` is CTA origin and is a closed set (EVENT_SOURCES) — typed so a
  // literal typo at a call site fails tsc instead of opening a Mixpanel bucket.
  properties?: Record<string, unknown> & { source?: EventSource };
  beacon?: boolean;
};

export type TrackFunction = (args: TrackArguments) => void;

/**
 * Analytics hook for tracking user interactions.
 * Integrates with Mixpanel via the Analytics service.
 * Automatically includes webreg_user_id and utm_params in all events (snake_case).
 *
 * Event name constants are the final snake_case Mixpanel names — track /
 * trackView / trackPopupView do not rewrite or prefix event strings (RND-37449).
 */
const useAnalytics = () => {
  const { webregUserId, utmParams } = useRegistration();

  // Session defaults are spread LAST deliberately — a call site cannot override
  // identity properties. utm_params is omitted rather than sent as null so most
  // events don't carry an empty property (PR #101 review).
  const sessionProperties = useCallback(
    () => ({
      webreg_user_id: webregUserId,
      ...(utmParams ? { utm_params: utmParams } : {}),
    }),
    [webregUserId, utmParams],
  );

  const track: TrackFunction = useCallback(
    ({ event, properties, beacon }) => {
      analytics.track(
        event,
        { ...properties, ...sessionProperties() },
        beacon ? { transport: 'sendBeacon' } : {},
      );
    },
    [sessionProperties],
  );

  // Alias of track — kept so call sites can signal "screen view" intent.
  const trackView: TrackFunction = useCallback(
    ({ event, properties }) => {
      track({ event, properties });
    },
    [track],
  );

  const trackError: TrackFunction = useCallback(
    ({ event, properties }) => {
      analytics.trackError(event, {
        ...properties,
        ...sessionProperties(),
      });
    },
    [sessionProperties],
  );

  const identifyUser = useCallback((analyticsID: string) => {
    analytics.identifyUser(analyticsID);
  }, []);

  const resetIdentity = useCallback(() => {
    analytics.reset();
  }, []);

  const setUserProperties = useCallback((properties: Record<string, unknown>) => {
    analytics.setUserProperties(properties);
  }, []);

  const setEntrySource = useCallback((source: EntrySource) => {
    analytics.setEntrySource(source);
  }, []);

  // Alias of track — kept so call sites can signal "popup view" intent.
  const trackPopupView: TrackFunction = useCallback(
    ({ event, properties }) => {
      track({ event, properties });
    },
    [track],
  );

  return {
    track,
    trackView,
    trackError,
    identifyUser,
    resetIdentity,
    setUserProperties,
    setEntrySource,
    trackPopupView,
  };
};

export { useAnalytics };
